"use client"

import Link from "next/link"
import { ArrowRight, CheckCircle2, LayoutDashboard } from "lucide-react"
import { useEffect, useRef } from "react"

import { Button } from "@/components/ui/button"
import { trackEvent } from "@/lib/analytics"
import {
  markSavedPlanDestination,
  pendingSavedPlanEventProperties,
  readPendingSavedPlanFlow,
  type SavedPlanAnalyticsContext,
} from "@/lib/saved-plan-analytics"
import { cn } from "@/lib/utils"

interface SavedPlanActionCardProps {
  eyebrow: string
  title: string
  firstAction: string
  actionMeta?: string
  analyticsContext: SavedPlanAnalyticsContext
  planDestination: string
  primaryLabel: string
  primaryDestination: string
  primaryHref?: string
  onPrimaryAction?: () => void
  secondaryHref?: string
  secondaryLabel?: string
  onSecondaryAction?: () => void
  className?: string
}

/**
 * Shared post-save receipt for Grass ID, Diagnose, and Weed ID.
 *
 * The receipt has one job: confirm that the result is durable, name the first
 * real action, and make the plan the unmistakable primary destination. The
 * dashboard is intentionally secondary because it is the return surface, not
 * the next step immediately after a scan.
 */
export function SavedPlanActionCard({
  eyebrow,
  title,
  firstAction,
  actionMeta,
  analyticsContext,
  planDestination,
  primaryLabel,
  primaryDestination,
  primaryHref,
  onPrimaryAction,
  secondaryHref = "/dashboard",
  secondaryLabel = "Go to dashboard",
  onSecondaryAction,
  className,
}: SavedPlanActionCardProps) {
  const impressionTracked = useRef(false)
  const primaryClassName =
    "min-h-[48px] w-full rounded-xl bg-linear-to-r from-green-600 to-emerald-600 px-5 font-semibold text-white shadow-[0_8px_24px_-8px_rgba(22,101,52,0.45)] transition-all hover:-translate-y-0.5 hover:from-green-700 hover:to-emerald-700 hover:shadow-[0_12px_32px_-8px_rgba(22,101,52,0.55)] active:translate-y-0 active:scale-[0.98] sm:w-auto"

  useEffect(() => {
    if (impressionTracked.current) return
    impressionTracked.current = true
    const properties = pendingSavedPlanEventProperties(analyticsContext, {
      destination: planDestination,
      post_auth_return: Boolean(readPendingSavedPlanFlow(analyticsContext.analysisId)),
    })
    trackEvent("saved_plan_destination_viewed", properties)
    trackEvent("saved_plan_receipt_shown", properties)
  }, [analyticsContext, planDestination])

  const handlePrimaryAction = () => {
    trackEvent("saved_plan_primary_clicked", pendingSavedPlanEventProperties(analyticsContext, {
      destination: primaryDestination,
    }))
    if (primaryHref) {
      markSavedPlanDestination(analyticsContext, primaryDestination, "primary")
    }
    onPrimaryAction?.()
    if (!primaryHref) {
      window.requestAnimationFrame(() => {
        trackEvent("saved_plan_action_destination_viewed", pendingSavedPlanEventProperties(analyticsContext, {
          action: "primary",
          destination: primaryDestination,
        }))
      })
    }
  }

  const handleSecondaryAction = () => {
    trackEvent("saved_plan_secondary_clicked", pendingSavedPlanEventProperties(analyticsContext, {
      destination: "dashboard",
    }))
    markSavedPlanDestination(analyticsContext, "dashboard", "secondary")
    onSecondaryAction?.()
  }

  return (
    <section
      aria-label={eyebrow}
      className={cn(
        "rounded-2xl border border-green-200/80 bg-white p-5 shadow-[0_1px_2px_rgba(16,24,16,0.04),0_12px_32px_-16px_rgba(22,101,52,0.2)] sm:p-6 md:p-8",
        className,
      )}
    >
      <div className="inline-flex items-center gap-1.5 rounded-full border border-green-200 bg-green-50 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.08em] text-green-700">
        <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
        {eyebrow}
      </div>

      <h2 className="mt-3 text-[1.375rem] font-extrabold leading-[1.15] tracking-[-0.02em] text-gray-900 md:text-[1.625rem] md:leading-[1.12]">
        {title}
      </h2>

      <div className="mt-4 rounded-xl border border-green-100 bg-green-50/60 p-4">
        <p className="text-[11px] font-bold uppercase tracking-[0.08em] text-green-700">
          First action
        </p>
        <p className="mt-1.5 text-[0.9375rem] font-semibold leading-relaxed text-gray-900">
          {firstAction}
        </p>
        {actionMeta ? (
          <p className="mt-1.5 text-xs leading-relaxed text-gray-500">{actionMeta}</p>
        ) : null}
      </div>

      <div className="mt-5 flex flex-col gap-2.5 sm:flex-row sm:items-center">
        {primaryHref ? (
          <Button asChild className={primaryClassName}>
            <Link href={primaryHref} onClick={handlePrimaryAction} prefetch={false}>
              {primaryLabel}
              <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
            </Link>
          </Button>
        ) : (
          <Button type="button" onClick={handlePrimaryAction} className={primaryClassName}>
            {primaryLabel}
            <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
          </Button>
        )}

        <Button asChild variant="outline" className="min-h-[44px] w-full rounded-xl border-green-200 font-semibold text-green-800 hover:bg-green-50 hover:text-green-900 sm:w-auto">
          <Link href={secondaryHref} onClick={handleSecondaryAction} prefetch={false}>
            <LayoutDashboard className="mr-2 h-4 w-4" aria-hidden="true" />
            {secondaryLabel}
          </Link>
        </Button>
      </div>
    </section>
  )
}
